import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Search,
  Plus,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Activity,
  AlertCircle,
  CheckCircle,
  SortAsc,
  SortDesc,
  User,
  Upload
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient as api } from '../lib/api';
import { AddPatientForm } from '../components/forms/AddPatientForm';
import { ImportPatientsCSV } from '../components/forms/ImportPatientsCSV';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import { useServerList } from '../hooks/useServerList';
import { Pagination } from '../components/common/Pagination';

const PAGE_SIZE = 50;

interface Patient {
  _id: string;
  physicianId: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  patientType?: 'human' | 'animal';
  animal?: { name?: string; species?: string; weightLbs?: number };
  phone: string;
  email?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  status: 'active' | 'inactive';
  medicalHistory?: string;
  allergies?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface PatientStats {
  totalPatients: number;
  newPatientsThisMonth: number;
  activePatients: number;
  totalHealthDataEntries: number;
}

export const PatientRoster: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PatientStats | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'dateOfBirth' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const canViewPatients = !!user && ['physician', 'physician_readonly'].includes(user.role);

  // Paged and searched on the server. The roster used to load the first 100
  // patients with no pager, so larger clinics could not see the rest.
  const list = useServerList<Patient, { sortBy: typeof sortBy; sortOrder: typeof sortOrder }>({
    fetchPage: ({ page, pageSize, search, filters, signal }) =>
      api.searchPatientsPage({ page, pageSize, search, signal, ...filters }),
    filters: { sortBy, sortOrder },
    pageSize: PAGE_SIZE,
    enabled: canViewPatients,
  });
  const patients = list.items;
  const loading = list.loading;
  const error = actionError || list.error;
  const searchQuery = list.search;

  useEffect(() => {
    if (canViewPatients) loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPatients]);

  const loadStats = async () => {
    try {
      const response = await api.getPatientStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load patient stats:', err);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    list.setSearch(e.target.value);
  };

  const handleSort = (newSortBy: 'name' | 'dateOfBirth' | 'createdAt') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const handleMarkPatientInactive = async (patientId: string) => {
    if (!confirm('Are you sure you want to mark this patient as inactive? This will remove them from the active patient list.')) {
      return;
    }

    try {
      setActionLoading(patientId);
      const response = await api.markPatientInactive(patientId);
      
      if (response.success) {
        setActionError(null);
        list.reload();
        loadStats(); // Refresh stats
      } else {
        setActionError(response.error || 'Failed to mark patient as inactive');
      }
    } catch (err: any) {
      setActionError(err.message || 'Failed to mark patient as inactive');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse as UTC to avoid timezone shift for date-only fields like birthdays
    const date = new Date(dateString);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return formatDateUtil(utcDate);
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const getAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    // Use UTC methods to avoid timezone issues with date-only fields
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
      age--;
    }
    
    return age;
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />;
  };

  const PatientCard: React.FC<{
    patient: Patient;
  }> = ({ patient }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start space-x-3 mb-4">
          <div className="flex-shrink-0 h-12 w-12 lg:h-10 lg:w-10">
            <div className="h-12 w-12 lg:h-10 lg:w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <User className="h-6 w-6 lg:h-5 lg:w-5 text-gray-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg lg:text-base font-medium text-gray-900 truncate">
              {patient.firstName} {patient.lastName}
            </h3>
            <p className="text-sm text-gray-500">
              {patient.patientType === 'animal'
                ? [patient.animal?.species, patient.animal?.name && `“${patient.animal.name}”`].filter(Boolean).join(' ') || 'Pet'
                : patient.dateOfBirth ? `${getAge(patient.dateOfBirth)} years old • DOB: ${formatDate(patient.dateOfBirth)}` : '—'}
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{formatPhoneNumber(patient.phone)}</span>
          </div>
          {patient.email && (
            <div className="flex items-center text-sm text-gray-600">
              <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">{patient.email}</span>
            </div>
          )}
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{patient.address.city}, {patient.address.state}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>Added: {formatDate(patient.createdAt)}</span>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => navigate(`/patients/${patient._id}`)}
              className="flex items-center justify-center px-4 py-3 lg:py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              View Profile
            </button>
            {user?.role === 'physician' && (
              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(`/patients/${patient._id}/edit`)}
                  className="flex-1 flex items-center justify-center px-4 py-3 lg:py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleMarkPatientInactive(patient._id)}
                  disabled={actionLoading === patient._id}
                  className="flex-1 flex items-center justify-center px-4 py-3 lg:py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 rounded-lg transition-colors min-h-[44px] lg:min-h-0"
                >
                  {actionLoading === patient._id ? 'Marking...' : 'Mark Inactive'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!user || !['physician', 'physician_readonly'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only physicians can access patient records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Patient Roster</h1>
                <p className="text-sm text-gray-600">Manage your patient records and medical data</p>
              </div>
            </div>
            
            {user.role === 'physician' && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowImportCSV(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </button>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Patient
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
            <div className="bg-white rounded-lg shadow-sm p-3 lg:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
                </div>
                <div className="ml-2 lg:ml-5 w-0 flex-1 min-w-0">
                  <dl>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Patients</dt>
                    <dd className="text-base lg:text-lg font-medium text-gray-900">{stats.totalPatients}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-3 lg:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserPlus className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
                </div>
                <div className="ml-2 lg:ml-5 w-0 flex-1 min-w-0">
                  <dl>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">New This Month</dt>
                    <dd className="text-base lg:text-lg font-medium text-gray-900">{stats.newPatientsThisMonth}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-3 lg:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
                </div>
                <div className="ml-2 lg:ml-5 w-0 flex-1 min-w-0">
                  <dl>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Active Patients</dt>
                    <dd className="text-base lg:text-lg font-medium text-gray-900">{stats.activePatients}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-3 lg:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-6 w-6 lg:h-8 lg:w-8 text-purple-600" />
                </div>
                <div className="ml-2 lg:ml-5 w-0 flex-1 min-w-0">
                  <dl>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Health Records</dt>
                    <dd className="text-base lg:text-lg font-medium text-gray-900">{stats.totalHealthDataEntries}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1 lg:max-w-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search patients by name, phone, or email..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base lg:text-sm"
                  />
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-2">
                <label className="text-sm font-medium text-gray-700 lg:whitespace-nowrap">Sort by:</label>
                <div className="flex items-center space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'dateOfBirth' | 'createdAt')}
                    className="border border-gray-300 rounded-lg px-3 py-3 lg:py-1 text-base lg:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 lg:flex-none"
                  >
                    <option value="createdAt">Date Added</option>
                    <option value="name">Name</option>
                    <option value="dateOfBirth">Age</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-2 lg:p-1 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg lg:rounded-md hover:bg-gray-50"
                  >
                    {getSortIcon(sortBy)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Patient List */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading patients...</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="p-12 text-center">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery ? 'Try adjusting your search terms.' : 'Get started by adding your first patient.'}
              </p>
              {user?.role === 'physician' && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Patient
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {patients.map((patient) => (
                  <PatientCard key={patient._id} patient={patient} />
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block data-table-container">
                <div className="data-table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('name')}>
                          <div className="flex items-center space-x-1">
                            <span>Patient</span>
                            {getSortIcon('name')}
                          </div>
                        </th>
                        <th className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('dateOfBirth')}>
                          <div className="flex items-center space-x-1">
                            <span>Age</span>
                            {getSortIcon('dateOfBirth')}
                          </div>
                        </th>
                        <th>Contact</th>
                        <th>Location</th>
                        <th className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('createdAt')}>
                          <div className="flex items-center space-x-1">
                            <span>Added</span>
                            {getSortIcon('createdAt')}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map((patient) => (
                        <tr key={patient._id}>
                          <td className="cell-primary">
                            <div className="cell-inline">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-600" />
                              </div>
                              <div className="cell-stack ml-4">
                                <span className="cell-stack-value">{patient.firstName} {patient.lastName}</span>
                                <span className="cell-stack-label">
                                  {patient.patientType === 'animal'
                                    ? [patient.animal?.species, patient.animal?.name].filter(Boolean).join(' • ') || 'Pet'
                                    : patient.dateOfBirth ? `DOB: ${formatDate(patient.dateOfBirth)}` : '—'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="cell-secondary">
                            {patient.patientType === 'animal'
                              ? (patient.animal?.weightLbs != null ? `${patient.animal.weightLbs} lb` : '—')
                              : patient.dateOfBirth ? `${getAge(patient.dateOfBirth)} years` : '—'}
                          </td>
                          <td className="cell-secondary">
                            <div className="cell-stack">
                              <span className="cell-inline">
                                <Phone className="cell-inline-icon" />
                                {formatPhoneNumber(patient.phone)}
                              </span>
                              {patient.email && (
                                <span className="cell-inline cell-stack-label">
                                  <Mail className="cell-inline-icon" />
                                  <span className="cell-truncate">{patient.email}</span>
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="cell-secondary">
                            <span className="cell-inline">
                              <MapPin className="cell-inline-icon" />
                              {patient.address.city}, {patient.address.state}
                            </span>
                          </td>
                          <td className="cell-secondary">
                            <span className="cell-inline">
                              <Calendar className="cell-inline-icon" />
                              {formatDate(patient.createdAt)}
                            </span>
                          </td>
                          <td className="cell-actions">
                            <div className="table-actions">
                              <button
                                onClick={() => navigate(`/patients/${patient._id}`)}
                                className="table-action-btn table-action-btn-primary"
                              >
                                View
                              </button>
                              {user?.role === 'physician' && (
                                <>
                                  <button
                                    onClick={() => navigate(`/patients/${patient._id}/edit`)}
                                    className="table-action-btn table-action-btn-secondary"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleMarkPatientInactive(patient._id)}
                                    disabled={actionLoading === patient._id}
                                    className="table-action-btn table-action-btn-danger"
                                  >
                                    {actionLoading === patient._id ? '...' : 'Inactive'}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {list.totalPages !== null && list.total !== null && (
                list.totalPages > 1 ? (
                  <Pagination
                    currentPage={list.page}
                    totalPages={list.totalPages}
                    onPageChange={list.setPage}
                    totalItems={list.total}
                    itemsPerPage={PAGE_SIZE}
                  />
                ) : (
                  <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-700">
                    {list.total} patient{list.total === 1 ? '' : 's'}{searchQuery ? ` matching “${searchQuery}”` : ''}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Patient Form */}
      {showAddForm && (
        <AddPatientForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            list.reload();
            loadStats(); // Refresh stats
          }}
        />
      )}

      {showImportCSV && (
        <ImportPatientsCSV
          onClose={() => setShowImportCSV(false)}
          onSuccess={() => {
            list.reload();
            loadStats();
          }}
        />
      )}
    </div>
  );
}; 